import { makeAutoObservable, runInAction } from 'mobx'
import { showToast } from 'shared/ui'
import { getEntries } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import {
  IntegrationsApi,
  type IParamsSettingDict_1_0_Value,
  type Integration,
  type ISettingRequestBySettingDict,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const ERROR_NOTIFICATION = 'We couldn’t update your preference. Please try again later.'
const SUCCESS_NOTIFICATION = 'Changes saved'

export class IntegrationCustomSettingsStore<T extends object> {
  private _status = new RequestLoadData()
  private _requestController = new AbortController()
  private _settingValues = new Map<keyof T, T[keyof T]>()
  private _settingsOptions = new Map<keyof T, { value: T[keyof T]; label: string }[]>()

  constructor(private _integration: Integration, private _entities?: IEntitiesStore) {
    makeAutoObservable(this)
  }

  async init() {
    this.getInitialData()
  }

  async init1_0() {
    this.getInitialData1_0()
  }

  getInitialData = () =>
    this._status.blockingLoadDataRequest({
      requestCallback: async () => {
        const [propsList, propsValue] = await Promise.all([
          IntegrationsApi.getIntegrationSettingsList<T>(this._integration.key),
          IntegrationsApi.getIntegrationSettings<T>(this._integration.key),
        ])

        propsValue.data.data.forEach(({ key, value }) => {
          this._settingValues.set(key, value)
        })

        Object.values(propsList.data).forEach((options) => {
          getEntries(options).forEach(([key, value]) => {
            this._settingsOptions.set(key, value)
          })
        })
      },
    })

  getInitialData1_0 = () => {
    return this._status.blockingLoadDataRequest({
      requestCallback: async () => {
        if (this._entities) {
          await this._entities.organizationSettingsStore.fetchOrganizationSettings()
        }

        const settings = this._entities?.organizationSettingsStore.getItem(
          `integrations-${this._integration.key}`
        )

        if (settings) {
          Object.keys(settings.settings).forEach((key) => {
            this._settingValues.set(key as keyof T, settings?.settings[key] as T[keyof T])
          })

          const propertiesMapping = settings.settings.properties_mapping
          if (Array.isArray(propertiesMapping)) {
            propertiesMapping.forEach((option) => {
              this._settingsOptions.set(option.key, option.value)
            })
          }
        }
      },
    })
  }

  patchData = (data: ISettingRequestBySettingDict<T>) => {
    runInAction(() => {
      data.forEach(({ key, value }) => {
        this._settingValues.set(key, value)
      })
    })

    return this._status.loadDataRequest({
      requestCallback: async () => {
        this._requestController.abort()
        this._requestController = new AbortController()
        const response = await IntegrationsApi.putIntegrationSettings<T>(
          this._integration.key,
          data,
          {
            signal: this._requestController.signal,
          }
        )
        runInAction(() => {
          response.data.data.forEach(({ key, value }) => {
            if (this._settingValues.get(key) !== value) {
              this._settingValues.set(key, value)
            }
          })
        })

        showToast({
          type: 'success',
          title: SUCCESS_NOTIFICATION,
        })

        return response
      },
      errorCallback: (e) => {
        showToast({
          type: 'error',
          title: ERROR_NOTIFICATION,
        })
      },
    })
  }

  patchMappingData_1_0 = (settingsKey: string, values: IParamsSettingDict_1_0_Value[]) => {
    runInAction(() => {
      this._settingValues.set(settingsKey as keyof T, values as T[keyof T])
    })

    return this._status.loadDataRequest({
      requestCallback: async () => {
        this._requestController.abort()
        this._requestController = new AbortController()
        const params = {
          featureKey: `integrations-${this._integration.key}`,
          settingsKey,
          value: values,
        }
        const response = await IntegrationsApi.putIntegrationSettings1_0(params, {
          signal: this._requestController.signal,
        })

        showToast({
          type: 'success',
          title: SUCCESS_NOTIFICATION,
        })

        return response
      },
      errorCallback: () => {
        showToast({
          type: 'error',
          title: ERROR_NOTIFICATION,
        })
      },
    })
  }

  patchSyncData1_0 = (settingsKey: string, value: boolean) => {
    runInAction(() => {
      this._settingValues.set(settingsKey as keyof T, value as T[keyof T])
    })

    return this._status.loadDataRequest({
      requestCallback: async () => {
        this._requestController.abort()
        this._requestController = new AbortController()
        const params = {
          featureKey: `integrations-${this._integration.key}`,
          settingsKey,
          value,
        }
        const response = await IntegrationsApi.putIntegrationSettings1_0(params, {
          signal: this._requestController.signal,
        })

        showToast({
          type: 'success',
          title: SUCCESS_NOTIFICATION,
        })

        return response
      },
      errorCallback: () => {
        showToast({
          type: 'error',
          title: ERROR_NOTIFICATION,
        })
      },
    })
  }

  getValue = <K extends keyof T>(key: K) => this._settingValues.get(key) as T[K]

  getOptions = <K extends keyof T>(key: K) =>
    this._settingsOptions.get(key) as Array<{ value: T[K]; label: string }>

  getValueOrFirstOption = <K extends keyof T>(key: K) => {
    return this.getValue(key) || this.getOptions(key)?.[0]?.value
  }

  setValue = <K extends keyof T>(key: K, value: T[K]) => {
    this._settingValues.set(key, value)
  }

  get status() {
    return this._status.status
  }
}
